// Vehicle maintenance schedule and history mock data.

export type MaintenanceKind =
  | "oil_change"
  | "tire_rotation"
  | "brake_service"
  | "inspection"
  | "battery"
  | "engine_tune"
  | "electrical"
  | "body_repair";

export type MaintenanceStatus = "scheduled" | "in_progress" | "completed" | "overdue";

export interface MaintenanceRecord {
  id: string;
  vehicleId: string;
  kind: MaintenanceKind;
  status: MaintenanceStatus;
  scheduledFor: string; // human date
  completedAt?: string;
  odometerKm: number;
  costNGN: number;
  provider: string;
  technician?: string;
  notes?: string;
}

export const maintenanceKindLabel: Record<MaintenanceKind, string> = {
  oil_change: "Oil change",
  tire_rotation: "Tire rotation",
  brake_service: "Brake service",
  inspection: "Safety inspection",
  battery: "Battery",
  engine_tune: "Engine tune-up",
  electrical: "Electrical",
  body_repair: "Body repair",
};

export const maintenanceRecords: MaintenanceRecord[] = [
  { id: "M-4001", vehicleId: "V-201", kind: "oil_change", status: "completed", scheduledFor: "May 12, 2026", completedAt: "May 12, 2026", odometerKm: 17200, costNGN: 12500, provider: "Yaba Bike Hub", technician: "Musa Idris", notes: "Fully synthetic 10W-40." },
  { id: "M-4002", vehicleId: "V-201", kind: "brake_service", status: "scheduled", scheduledFor: "Jul 18, 2026", odometerKm: 18420, costNGN: 22000, provider: "Yaba Bike Hub" },
  { id: "M-4003", vehicleId: "V-201", kind: "tire_rotation", status: "scheduled", scheduledFor: "Aug 02, 2026", odometerKm: 18420, costNGN: 8500, provider: "Yaba Bike Hub" },

  { id: "M-4010", vehicleId: "V-202", kind: "inspection", status: "completed", scheduledFor: "Jun 02, 2026", completedAt: "Jun 02, 2026", odometerKm: 86800, costNGN: 35000, provider: "Toyota Nigeria", technician: "K. Adebayo" },
  { id: "M-4011", vehicleId: "V-202", kind: "oil_change", status: "overdue", scheduledFor: "Jun 28, 2026", odometerKm: 88240, costNGN: 24000, provider: "Toyota Nigeria", notes: "Diesel filter also flagged." },
  { id: "M-4012", vehicleId: "V-202", kind: "brake_service", status: "scheduled", scheduledFor: "Jul 22, 2026", odometerKm: 88240, costNGN: 68000, provider: "Toyota Nigeria" },

  { id: "M-4020", vehicleId: "V-203", kind: "inspection", status: "completed", scheduledFor: "Jun 18, 2026", completedAt: "Jun 18, 2026", odometerKm: 8900, costNGN: 6500, provider: "Lekki Auto" },
  { id: "M-4021", vehicleId: "V-203", kind: "oil_change", status: "scheduled", scheduledFor: "Jul 30, 2026", odometerKm: 9420, costNGN: 11000, provider: "Lekki Auto" },

  { id: "M-4030", vehicleId: "V-204", kind: "engine_tune", status: "in_progress", scheduledFor: "Jul 03, 2026", odometerKm: 42110, costNGN: 45000, provider: "Ikeja Motorworks", technician: "Chuka O.", notes: "Rough idle after cold start." },
  { id: "M-4031", vehicleId: "V-204", kind: "electrical", status: "completed", scheduledFor: "Apr 22, 2026", completedAt: "Apr 22, 2026", odometerKm: 39800, costNGN: 18000, provider: "Ikeja Motorworks" },

  { id: "M-4040", vehicleId: "V-205", kind: "battery", status: "scheduled", scheduledFor: "Jul 10, 2026", odometerKm: 132400, costNGN: 42000, provider: "Ford Service Center" },
  { id: "M-4041", vehicleId: "V-205", kind: "oil_change", status: "completed", scheduledFor: "Mar 30, 2026", completedAt: "Mar 30, 2026", odometerKm: 128900, costNGN: 26000, provider: "Ford Service Center", technician: "T. Balogun" },

  { id: "M-4050", vehicleId: "V-206", kind: "oil_change", status: "completed", scheduledFor: "May 28, 2026", completedAt: "May 28, 2026", odometerKm: 19800, costNGN: 10500, provider: "Maryland Bikes" },
  { id: "M-4051", vehicleId: "V-206", kind: "tire_rotation", status: "scheduled", scheduledFor: "Aug 12, 2026", odometerKm: 21050, costNGN: 7500, provider: "Maryland Bikes" },

  { id: "M-4060", vehicleId: "V-207", kind: "inspection", status: "completed", scheduledFor: "Jun 10, 2026", completedAt: "Jun 10, 2026", odometerKm: 212400, costNGN: 78000, provider: "Isuzu Motors", technician: "F. Adeoye" },
  { id: "M-4061", vehicleId: "V-207", kind: "brake_service", status: "scheduled", scheduledFor: "Jul 25, 2026", odometerKm: 214800, costNGN: 115000, provider: "Isuzu Motors" },
  { id: "M-4062", vehicleId: "V-207", kind: "body_repair", status: "completed", scheduledFor: "Feb 14, 2026", completedAt: "Feb 16, 2026", odometerKm: 205200, costNGN: 240000, provider: "Panel Beaters Ltd", notes: "Rear door dent from loading incident." },

  { id: "M-4070", vehicleId: "V-208", kind: "inspection", status: "completed", scheduledFor: "Jun 24, 2026", completedAt: "Jun 24, 2026", odometerKm: 2800, costNGN: 15000, provider: "BYD Nigeria" },
  { id: "M-4071", vehicleId: "V-208", kind: "battery", status: "scheduled", scheduledFor: "Dec 01, 2026", odometerKm: 3120, costNGN: 0, provider: "BYD Nigeria", notes: "Annual battery health check (warranty)." },
];

export function statusTone(s: MaintenanceStatus) {
  return {
    scheduled: "bg-info/10 text-info border-info/20",
    in_progress: "bg-warning/15 text-warning-foreground border-warning/30",
    completed: "bg-success/10 text-success border-success/20",
    overdue: "bg-destructive/10 text-destructive border-destructive/20",
  }[s];
}

export const statusLabelMap: Record<MaintenanceStatus, string> = {
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed: "Completed",
  overdue: "Overdue",
};
